import React, { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { Link, useHistory, useLocation } from "react-router-dom";
import { Button, DropdownItem, DropdownMenu, DropdownToggle, Spinner, Table, UncontrolledDropdown } from "reactstrap";
import Select from "react-select";
import moment from "moment";

import { useAuth } from "../../../hooks/auth";
import { useCompanies } from "../../../hooks/companies";
import { useDateFilter } from "../../../hooks/datefilter";
import PageHero, {
  PageHeroExportButton,
  PageHeroRefreshButton,
} from "../../../components/PageHero";
import FilterBand from "../../../components/FilterBand";
import MetricCard from "../../../components/MetricCard";
import MetricCardGroup from "../../../components/MetricCardGroup";
import ShellCard, { TableSurface } from "../../../components/ShellCard";
import TablePagination from "../../../components/TablePagination";
import { AUTOPILOT_COLORS } from "../../../components/autopilot/styles";
import { formatDate } from "../../../utils/formatDate";
import { exportCsv } from "../../../utils/exportCsv";
import { downloadBlob } from "../../../utils/downloadBlob";
import { makeReactSelectStyles, reactSelectPortalProps } from "../../../utils/reactSelectStyles";
import {
  MAX_MARCADORES_FILTRO,
  PERMISSOES,
  adicionarAoLote,
  arquivoEtiquetas,
  excedeLote,
  gerarNotfis,
  lerErroBlob,
  listarMarcadores,
  listarPedidos,
  marcadoresDaLinha,
  mensagemErroPedido,
  notasDosPedidos,
  transportadorasDasNotas,
} from "../../../services/pedidosApi";
import { temPermissao } from "../AuditoriaPedido/regras";
import { Aviso, CheckboxTodos, SelecaoResumo, fmtNumber, useSelecao } from "../_documentosFrete/comum";
import "../_documentosFrete/styles.css";
import DetalhePedido from "./DetalhePedido";
import NotasDoPedido from "./NotasDoPedido";
import PedidoOperacionalModal from "./PedidoOperacionalModal";
import MarcadoresModal from "./MarcadoresModal";
import ComprovantesModal from "./ComprovantesModal";
import TrocarTransportadoraModal from "./TrocarTransportadoraModal";
import HistoricoModal from "./HistoricoModal";
import EtiquetasModal from "./EtiquetasModal";

import "./styles.css";

// Trackfy › Operação › Pedidos: as notas fiscais do embarcador agrupadas pelo
// número do pedido (NotaFiscal.Num_Pedido). Substitui o protótipo que mostrava
// três pedidos fixos no código.
//
// O status do pedido é CONSOLIDADO no backend (/trackfy/pedidos) a partir do
// status canônico de cada NF (PO1-2820): pedido, NF, Entregas Pendentes e KPI
// usam a mesma regra. A tela não deriva status de data nenhuma.
//
// Gestão de Pedidos (épico PO1-2374, sprint 612): seleção em massa com barra de
// ações (marcadores, NOTFIS, romaneio, etiquetas, lote), menu por linha (dados
// operacionais, marcadores, comprovantes, troca de transportadora, histórico),
// chips de marcador, badge de etiquetas e filtro de marcadores. Cada ação só
// aparece com a permissão hub.operacao.* correspondente (temPermissao), menos
// as de etiqueta (gerar, re-tentar, imprimir): quem vê a tela gera etiquetas.

// Ordem dos cards = precedência da regra (o pior primeiro). `devolvido` só
// aparece quando existe no recorte: é raro e ocuparia um card vazio.
export const STATUS_PEDIDO = [
  {
    valor: "atrasado",
    rotulo: "Atrasado",
    tom: "pink",
    icone: "tim-icons icon-alert-circle-exc",
    ajuda: "Pelo menos uma nota com a previsão vencida e sem entrega.",
  },
  {
    valor: "parcial",
    rotulo: "Entrega parcial",
    tom: "warning",
    icone: "tim-icons icon-puzzle-10",
    ajuda: "Parte das notas entregue, parte ainda em aberto.",
  },
  {
    valor: "em_transito",
    rotulo: "A caminho",
    tom: "info",
    icone: "tim-icons icon-delivery-fast",
    ajuda: "Notas com ocorrência e previsão ainda válida.",
  },
  {
    // PO1-3401: entrega feita pela própria empresa, sem transportadora.
    valor: "entrega_propria",
    rotulo: "Entrega própria",
    tom: "info",
    icone: "tim-icons icon-bus-front-12",
    ajuda: "Notas entregues pela própria empresa, aguardando a baixa da entrega.",
    somenteSeHouver: true,
  },
  {
    // PO1-2375 (H1-B): estados pré-entrega derivados do lote de despacho e do
    // envio (NotaFiscalEnvio). Só aparecem quando existem no recorte.
    valor: "despachado",
    rotulo: "Despachado",
    tom: "info",
    icone: "tim-icons icon-send",
    ajuda: "Notas em lote de despacho já despachado, sem ocorrência da transportadora ainda.",
    somenteSeHouver: true,
  },
  {
    valor: "etiqueta_emitida",
    rotulo: "Etiqueta emitida",
    tom: "neutral",
    icone: "tim-icons icon-tag",
    ajuda: "Etiqueta gerada na transportadora, aguardando o despacho.",
    somenteSeHouver: true,
  },
  {
    valor: "pendente",
    rotulo: "Em preparação",
    tom: "neutral",
    icone: "tim-icons icon-bag-16",
    ajuda: "Sem ocorrência ainda e previsão no futuro.",
  },
  {
    valor: "sem_previsao",
    rotulo: "Sem previsão",
    tom: "warning",
    icone: "tim-icons icon-calendar-60",
    ajuda: "Nenhuma nota do pedido tem previsão calculada.",
  },
  {
    valor: "entregue",
    rotulo: "Entregue",
    tom: "success",
    icone: "tim-icons icon-check-2",
    ajuda: "Todas as notas do pedido entregues.",
  },
  {
    valor: "devolvido",
    rotulo: "Devolvido",
    tom: "pink",
    icone: "tim-icons icon-refresh-01",
    ajuda: "Todas as notas do pedido devolvidas.",
    somenteSeHouver: true,
  },
];

const TOM_DO_STATUS = STATUS_PEDIDO.reduce((acc, item) => {
  acc[item.valor] = item.tom;
  return acc;
}, {});

const PAGE_SIZES = [25, 50, 100];
const DEFAULT_PAGE_SIZE = 25;
const DIAS_PADRAO = 90;
// Romaneio e Lotes também ficam no Trackfy (PO1-3490): o despacho segue no
// mesmo produto, sem trocar para o Flow.
export const ROTA_ROMANEIO = "/app/trackfy/despacho/romaneio";
export const ROTA_LOTES = "/app/trackfy/despacho/lotes";

const selectStyles = makeReactSelectStyles({ minHeight: 38, radius: 10 });

const fmtNumero = (v) =>
  v === null || v === undefined ? "-" : Number(v).toLocaleString("pt-BR");

const fmtData = (v) => (v ? formatDate(v) : null);

// "Fulano" / "Fulano e mais 2": o backend manda um exemplo + a contagem de
// distintos, para a linha não mentir quando o pedido tem mais de um.
const comMais = (valor, quantidade) => {
  if (!valor) return "-";
  const extras = Number(quantidade || 0) - 1;
  return extras > 0 ? `${valor} e mais ${extras}` : valor;
};

// Emissão do pedido: uma data, ou o intervalo entre a primeira e a última NF.
// No mesmo ano, a primeira sai sem o ano ("04/09 a 29/09/2026") para caber.
const periodoEmissao = (primeira, ultima) => {
  const fim = fmtData(ultima);
  const inicio = fmtData(primeira);
  if (!fim) return "-";
  if (!inicio || inicio === fim) return fim;
  const mesmoAno = inicio.slice(-4) === fim.slice(-4);
  return `${mesmoAno ? inicio.slice(0, 5) : inicio} a ${fim}`;
};

const destinoDe = (cidade, uf) =>
  cidade ? `${cidade}${uf ? `/${uf}` : ""}` : null;

// Validações da seleção antes de NOTFIS / romaneio (regras puras, testáveis).
export function validarNotfis(notas) {
  if (!notas.length) return "Nenhuma nota encontrada para os pedidos selecionados.";
  const transportadoras = transportadorasDasNotas(notas);
  if (transportadoras.some((t) => t.id === null)) return "Há nota sem transportadora definida; o NOTFIS é por transportadora.";
  if (transportadoras.length > 1) {
    return `Selecione pedidos de uma única transportadora para gerar o NOTFIS. Na seleção: ${transportadoras.map((t) => t.nome).join(", ")}.`;
  }
  return "";
}

export function validarRomaneio(notas) {
  if (!notas.length) return "Nenhuma nota encontrada para os pedidos selecionados.";
  const transportadoras = transportadorasDasNotas(notas);
  if (transportadoras.some((t) => t.id === null)) return "Há nota sem transportadora definida; escolha a transportadora antes de criar o romaneio.";
  if (transportadoras.length > 1) {
    return `O romaneio é de uma transportadora só. Na seleção: ${transportadoras.map((t) => t.nome).join(", ")}.`;
  }
  const emRomaneio = notas.filter((n) => n.idRomaneio);
  if (emRomaneio.length) {
    return `Há nota já em romaneio: ${emRomaneio.map((n) => `NF ${n.numero} (#${n.idRomaneio})`).join(", ")}. Desvincule antes ou selecione outros pedidos.`;
  }
  return "";
}

// Badge "etiquetas emitidas/falhas" da linha (H5). Nada quando não há envio.
const BadgeEtiquetas = ({ p }) => {
  const emitidas = Number(p.etiquetas_emitidas || 0);
  const falhas = Number(p.etiquetas_falhas || 0);
  const total = Number(p.etiquetas_total || 0);
  if (!emitidas && !falhas && !total) return null;
  const tom = falhas > 0 ? "pink" : emitidas > 0 ? "success" : "neutral";
  const texto = falhas > 0 ? `${emitidas} etiqueta(s) · ${falhas} falha(s)` : `${emitidas} etiqueta(s)`;
  return (
    <span
      className={`pedidos__etiquetas pedidos__etiquetas--${tom}`}
      title={`Etiquetas: ${emitidas} emitida(s), ${falhas} com falha, ${total} envio(s) no total`}
    >
      <i className="tim-icons icon-tag" aria-hidden="true" /> {texto}
    </span>
  );
};

// Link direto (PO1-3493): `?busca=<texto>` chega já filtrado (atalho "Pedido"
// da tela de Indicadores de despacho). Só semeia o primeiro render.
const buscaDaUrl = (search) => {
  const valor = new URLSearchParams(search || "").get("busca");
  return valor ? valor.trim() : "";
};

const Pedido = () => {
  const { user } = useAuth();
  const history = useHistory();
  const location = useLocation();
  const { empresaId, transportadoraId, arrayParams } = useCompanies();
  const { startDate, endDate } = useDateFilter();

  const pode = useMemo(
    () => ({
      editar: temPermissao(user, PERMISSOES.EDITAR),
      transportadora: temPermissao(user, PERMISSOES.TRANSPORTADORA),
      notfis: temPermissao(user, PERMISSOES.NOTFIS),
      romaneio: temPermissao(user, PERMISSOES.ROMANEIO),
      despacho: temPermissao(user, PERMISSOES.DESPACHO),
    }),
    [user]
  );
  // Etiquetas valem para quem vê a tela, então a seleção sempre existe.
  const temAcaoEmMassa = true;

  const [itens, setItens] = useState([]);
  const [total, setTotal] = useState(0);
  const [resumo, setResumo] = useState({});
  const [notasSemPedido, setNotasSemPedido] = useState(0);
  const [pagina, setPagina] = useState(1);
  const [tamanhoPagina, setTamanhoPagina] = useState(DEFAULT_PAGE_SIZE);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState("");
  const [statusSelecionados, setStatusSelecionados] = useState([]);
  const [marcadoresSelecionados, setMarcadoresSelecionados] = useState([]);
  const [marcadoresDisponiveis, setMarcadoresDisponiveis] = useState([]);
  const [buscaRascunho, setBuscaRascunho] = useState(() => buscaDaUrl(location.search));
  const [busca, setBusca] = useState(() => buscaDaUrl(location.search));
  const [aberto, setAberto] = useState(null);
  const [versaoDetalhe, setVersaoDetalhe] = useState(0);
  const [modal, setModal] = useState(null); // { tipo, pedido?, pedidos?, marcadores?, modo? }
  const [aviso, setAviso] = useState(null); // { tom, texto, link?: { para, rotulo } }
  const [acaoEmCurso, setAcaoEmCurso] = useState(null); // 'notfis' | 'romaneio' | 'lote' | 'pdf' | 'zpl'
  const selecao = useSelecao();

  // Fora do dashboard o filtro global costuma vir vazio; sem janela padrão a
  // consulta varreria a NotaFiscal inteira (mesma regra de Entregas Pendentes).
  const periodo = useMemo(() => {
    const inicio = startDate ? moment(startDate) : moment().subtract(DIAS_PADRAO, "days");
    const fim = endDate ? moment(endDate) : moment();

    return {
      dataInicial: inicio.format("YYYY-MM-DD 00:00:00"),
      dataFinal: fim.format("YYYY-MM-DD 23:59:59"),
    };
  }, [startDate, endDate]);

  // Embarcador sai do token (req.user.embarcadorId); transportadora é filtro.
  const paramsRecorte = useMemo(() => {
    const params = { ...periodo };
    if (arrayParams.transportadoras) params.transportadoras = arrayParams.transportadoras;
    return params;
  }, [periodo, arrayParams.transportadoras]);

  const carregar = useCallback(async () => {
    setLoading(true);
    setErro("");

    try {
      const params = { ...paramsRecorte, pagina, tamanhoPagina };
      if (statusSelecionados.length) params.status = statusSelecionados;
      if (marcadoresSelecionados.length) params.marcadores = marcadoresSelecionados;
      if (busca) params.busca = busca;

      const data = await listarPedidos(params);
      setItens(data.itens || []);
      setTotal(data.total || 0);
      setResumo(data.resumo || {});
      setNotasSemPedido(data.notasSemPedido || 0);
    } catch (_) {
      setItens([]);
      setTotal(0);
      setResumo({});
      setErro("Não conseguimos carregar os pedidos agora.");
    } finally {
      setLoading(false);
    }
  }, [paramsRecorte, pagina, tamanhoPagina, statusSelecionados, marcadoresSelecionados, busca]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  // Opções do filtro de marcadores (os do embarcador). Recarrega por embarcador
  // e depois de uma ação de marcadores (versaoDetalhe).
  useEffect(() => {
    let ativo = true;
    listarMarcadores()
      .then((r) => ativo && setMarcadoresDisponiveis(r.map((m) => m.marcador).filter(Boolean)))
      .catch(() => ativo && setMarcadoresDisponiveis([]));
    return () => {
      ativo = false;
    };
  }, [empresaId, versaoDetalhe]);

  useEffect(() => {
    setPagina(1);
    setAberto(null);
  }, [empresaId, transportadoraId, periodo, statusSelecionados, marcadoresSelecionados, busca]);

  // Troca de embarcador: a seleção era de outro recorte.
  useEffect(() => {
    selecao.limpar();
    setAviso(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empresaId]);

  useEffect(() => {
    setAberto(null);
  }, [pagina, tamanhoPagina]);

  const alternarStatus = useCallback((valor) => {
    setStatusSelecionados((atual) =>
      atual.includes(valor) ? atual.filter((item) => item !== valor) : [...atual, valor]
    );
  }, []);

  const limpar = useCallback(() => {
    setBuscaRascunho("");
    setBusca("");
    setStatusSelecionados([]);
    setMarcadoresSelecionados([]);
  }, []);

  const linhas = useMemo(
    () =>
      itens.map((p) => {
        const encerradas = Number(p.notas_entregues || 0) + Number(p.notas_devolvidas || 0);
        return {
          ...p,
          id: p.pedido, // chave da seleção (useSelecao)
          marcadoresLista: marcadoresDaLinha(p.marcadores),
          destinatarioExibido: comMais(p.destinatario, p.quantidade_destinatarios),
          destinoExibido: comMais(destinoDe(p.cidade_entrega, p.uf_entrega), p.quantidade_destinos),
          transportadoraExibida: comMais(p.transportadora, p.quantidade_transportadoras),
          emissaoExibida: periodoEmissao(p.primeira_emissao, p.ultima_emissao),
          previsaoExibida: fmtData(p.previsao_entrega) || "Sem previsão",
          encerradas,
          progresso: p.quantidade_notas ? Math.round((encerradas / p.quantidade_notas) * 100) : 0,
        };
      }),
    [itens]
  );

  const exportar = useCallback(() => {
    exportCsv({
      filename: "pedidos",
      columns: [
        { key: "pedido", label: "Pedido" },
        { key: "quantidade_notas", label: "Notas" },
        { key: "encerradas", label: "Entregues ou devolvidas" },
        { key: "destinatarioExibido", label: "Destinatário" },
        { key: "destinoExibido", label: "Destino" },
        { key: "transportadoraExibida", label: "Transportadora" },
        { key: "emissaoExibida", label: "Emissão" },
        { key: "previsaoExibida", label: "Previsão" },
        { key: "dias_em_aberto", label: "Dias em aberto" },
        { key: "status_rotulo", label: "Status" },
        { key: "marcadores", label: "Marcadores" },
        { key: "codigo_rastreio_informado", label: "Código de rastreio" },
        { key: "etiquetas_emitidas", label: "Etiquetas emitidas" },
        { key: "etiquetas_falhas", label: "Etiquetas com falha" },
      ],
      rows: linhas,
    });
  }, [linhas]);

  // ─── Ações em massa ─────────────────────────────────────────────────────────
  const idsSelecionados = selecao.ids;
  // "Re-tentar" olha os ITENS atuais (a lista recarrega depois de cada ação), e
  // não o objeto guardado na seleção, que ficaria com etiquetas_falhas velho.
  const algumComFalha = useMemo(() => {
    const marcados = new Set(idsSelecionados.map(String));
    return itens.some((p) => marcados.has(String(p.pedido)) && Number(p.etiquetas_falhas || 0) > 0);
  }, [itens, idsSelecionados]);

  // Depois de uma ação que muda dados: recarrega a lista (chips, badges) e o
  // detalhe aberto.
  const fecharModal = (alterou) => {
    setModal(null);
    if (alterou) {
      setVersaoDetalhe((v) => v + 1);
      carregar();
    }
  };

  // Antes de qualquer ação em massa: o backend recusa mais de MAX_PEDIDOS_LOTE.
  const bloqueadoPorLimite = () => {
    const texto = excedeLote(idsSelecionados.length);
    if (texto) setAviso({ tom: "erro", texto });
    return Boolean(texto);
  };

  const abrirModal = (tipo, extra) => {
    setAviso(null);
    if (extra && Array.isArray(extra.pedidos) && extra.pedidos.length > 1 && bloqueadoPorLimite()) return;
    setModal({ tipo, ...(extra || {}) });
  };

  const executarEmMassa = async (nome, fn) => {
    if (acaoEmCurso) return;
    setAviso(null);
    if (bloqueadoPorLimite()) return;
    setAcaoEmCurso(nome);
    try {
      await fn();
    } catch (e) {
      const corpo = await lerErroBlob(e);
      const texto =
        corpo && corpo.error
          ? corpo.transportadoras && corpo.transportadoras.length
            ? `${corpo.error} (${corpo.transportadoras.map((t) => t.nome).join(", ")})`
            : corpo.error
          : mensagemErroPedido(e, "Não foi possível concluir a ação.");
      setAviso({ tom: "erro", texto });
    } finally {
      setAcaoEmCurso(null);
    }
  };

  const gerarNotfisSelecao = () =>
    executarEmMassa("notfis", async () => {
      const notas = await notasDosPedidos(idsSelecionados);
      const bloqueio = validarNotfis(notas);
      if (bloqueio) {
        setAviso({ tom: "erro", texto: bloqueio });
        return;
      }
      const { blob, nomeArquivo } = await gerarNotfis(idsSelecionados);
      downloadBlob(blob, nomeArquivo, "text/plain");
      setAviso({ tom: "ok", texto: `NOTFIS ${nomeArquivo} gerado com ${fmtNumber(notas.length)} nota(s) de ${fmtNumber(idsSelecionados.length)} pedido(s).` });
      setVersaoDetalhe((v) => v + 1);
    });

  const criarRomaneioSelecao = () =>
    executarEmMassa("romaneio", async () => {
      const notas = await notasDosPedidos(idsSelecionados);
      const bloqueio = validarRomaneio(notas);
      if (bloqueio) {
        setAviso({ tom: "erro", texto: bloqueio });
        return;
      }
      history.push(ROTA_ROMANEIO, {
        idsNotasIniciais: notas.map((n) => n.idNota),
        idTransportadora: notas[0].idTransportadora,
      });
    });

  const imprimirEtiquetas = (formato) =>
    executarEmMassa(formato, async () => {
      const { blob, nomeArquivo, ignoradas } = await arquivoEtiquetas(idsSelecionados, formato);
      downloadBlob(blob, nomeArquivo, formato === "zpl" ? "text/plain" : "application/pdf");
      setAviso({
        tom: ignoradas.length ? "aviso" : "ok",
        texto:
          `Arquivo ${nomeArquivo} gerado.` +
          (ignoradas.length ? ` Ignorada(s): ${ignoradas.map((i) => `NF ${i.numero} (${i.motivo})`).join(", ")}.` : ""),
      });
    });

  const adicionarAoLoteSelecao = () =>
    executarEmMassa("lote", async () => {
      const r = await adicionarAoLote(idsSelecionados);
      const lotes = (r && r.lotes) || [];
      const ignorados = (r && r.ignorados) || [];
      setAviso({
        tom: ignorados.length ? "aviso" : "ok",
        texto:
          `${fmtNumber(r.adicionados || 0)} envio(s) adicionado(s)` +
          (lotes.length ? ` ao(s) lote(s) ${lotes.map((l) => `${l.codigo} (${l.transportadora}${l.fechadoPorVolume ? ", fechado por volume" : ""})`).join(", ")}` : "") +
          (ignorados.length ? ` · ignorado(s): ${ignorados.map((i) => `${i.pedido ? `pedido ${i.pedido}` : `NF ${i.numero}`}: ${i.motivo}`).join("; ")}` : "") +
          ".",
        link: lotes.length ? { para: ROTA_LOTES, rotulo: "Ver lotes" } : null,
      });
      setVersaoDetalhe((v) => v + 1);
      carregar();
    });

  // ─── Render ─────────────────────────────────────────────────────────────────
  const cards = STATUS_PEDIDO.filter(
    (item) => !item.somenteSeHouver || (resumo[item.valor] && resumo[item.valor].pedidos > 0)
  );
  const totalNoPeriodo = STATUS_PEDIDO.reduce(
    (soma, item) => soma + ((resumo[item.valor] && resumo[item.valor].pedidos) || 0),
    0
  );
  const filtrosAtivos = statusSelecionados.length + (busca ? 1 : 0) + (marcadoresSelecionados.length ? 1 : 0);
  const periodoLegivel = `${moment(periodo.dataInicial).format("DD/MM/YYYY")} a ${moment(
    periodo.dataFinal
  ).format("DD/MM/YYYY")}`;
  const opcoesMarcadores = marcadoresDisponiveis.map((m) => ({ value: m, label: m }));
  const colunas = 12;

  const menuDaLinha = (p) => (
    <UncontrolledDropdown>
      <DropdownToggle size="sm" color="primary" className="pedidos__menu-toggle btn-simple" aria-label={`Ações do pedido ${p.pedido}`}>
        <i className="tim-icons icon-settings-gear-63" aria-hidden="true" />
      </DropdownToggle>
      <DropdownMenu right className="pedidos__menu">
        {pode.editar ? <DropdownItem onClick={() => abrirModal("operacional", { pedido: p.pedido })}>Editar dados operacionais</DropdownItem> : null}
        {pode.editar ? (
          <DropdownItem onClick={() => abrirModal("marcadores", { pedidos: [p.pedido], marcadores: p.marcadoresLista })}>Marcadores</DropdownItem>
        ) : null}
        <DropdownItem onClick={() => abrirModal("comprovantes", { pedido: p.pedido })}>Comprovantes</DropdownItem>
        {pode.transportadora ? <DropdownItem onClick={() => abrirModal("transportadora", { pedido: p.pedido })}>Trocar transportadora</DropdownItem> : null}
        <DropdownItem onClick={() => abrirModal("historico", { pedido: p.pedido })}>Histórico</DropdownItem>
      </DropdownMenu>
    </UncontrolledDropdown>
  );

  return (
    <div className="content pedidos">
      <PageHero
        eyebrow="Trackfy"
        title="Pedidos"
        description={`Notas fiscais agrupadas por pedido, emitidas entre ${periodoLegivel}. Ajuste a janela no botão Período, no topo da página.`}
        actions={
          <>
            <PageHeroRefreshButton onClick={carregar} loading={loading} />
            <PageHeroExportButton onClick={exportar} disabled={!linhas.length} />
          </>
        }
      />

      <MetricCardGroup
        title="Situação dos pedidos"
        description="Clique num indicador para filtrar a lista abaixo."
        action={<span className="pedidos__contagem">{fmtNumero(totalNoPeriodo)} no período</span>}
      >
        {cards.map((item) => (
          <MetricCard
            key={item.valor}
            label={item.rotulo}
            value={fmtNumero((resumo[item.valor] && resumo[item.valor].pedidos) || 0)}
            helper={item.ajuda}
            iconClassName={item.icone}
            tone={item.tom}
            onClick={() => alternarStatus(item.valor)}
            active={statusSelecionados.includes(item.valor)}
          />
        ))}
      </MetricCardGroup>

      <FilterBand
        mode="server"
        searchLabel="Buscar pedido"
        searchPlaceholder="Pedido, nota fiscal ou destinatário"
        searchHint="Achar uma nota traz o pedido inteiro."
        searchValue={buscaRascunho}
        onSearchChange={setBuscaRascunho}
        onSearchSubmit={setBusca}
        activeFiltersCount={filtrosAtivos}
        panelTitle="Marcadores"
        panelDescription={`Pedidos com qualquer um dos marcadores escolhidos (até ${MAX_MARCADORES_FILTRO}). O filtro aplica na hora.`}
        defaultOpen={marcadoresSelecionados.length > 0}
        extra={
          filtrosAtivos ? (
            <Button size="sm" color="default" outline onClick={limpar} disabled={loading}>
              Limpar filtros
            </Button>
          ) : null
        }
      >
        <div className="pedidos__filtro-marcadores">
          <label htmlFor="pedidos-filtro-marcadores">Marcadores</label>
          <Select
            inputId="pedidos-filtro-marcadores"
            isMulti
            classNamePrefix="react-select"
            styles={selectStyles}
            options={opcoesMarcadores}
            value={marcadoresSelecionados.map((m) => ({ value: m, label: m }))}
            onChange={(v) => setMarcadoresSelecionados((v || []).slice(0, MAX_MARCADORES_FILTRO).map((o) => o.value))}
            isOptionDisabled={() => marcadoresSelecionados.length >= MAX_MARCADORES_FILTRO}
            placeholder={opcoesMarcadores.length ? "Escolha um ou mais marcadores..." : "Nenhum marcador cadastrado neste embarcador"}
            noOptionsMessage={() =>
              marcadoresSelecionados.length >= MAX_MARCADORES_FILTRO ? `No máximo ${MAX_MARCADORES_FILTRO} marcadores no filtro` : "Nenhum marcador"
            }
            isDisabled={loading && !opcoesMarcadores.length}
            {...reactSelectPortalProps()}
          />
        </div>
      </FilterBand>

      <ShellCard
        title="Pedidos"
        description={`${fmtNumero(total)} pedido(s) no recorte atual. Clique num pedido para ver as notas.`}
      >
        {notasSemPedido > 0 ? (
          <div className="pedidos__aviso">
            <i className="tim-icons icon-alert-circle-exc" aria-hidden="true" />
            <span>
              {fmtNumero(notasSemPedido)} nota(s) do período não têm número de pedido e não
              aparecem aqui. Veja em{" "}
              <Link to="/app/trackfy/operacao/notas-fiscais">Consulta NFe</Link>.
            </span>
          </div>
        ) : null}

        {aviso ? (
          <Aviso tom={aviso.tom}>
            {aviso.texto}
            {aviso.link ? (
              <Fragment>
                {" "}
                <Link to={aviso.link.para}>{aviso.link.rotulo}</Link>
              </Fragment>
            ) : null}
          </Aviso>
        ) : null}

        <TableSurface>
          {selecao.totais.qtd > 0 ? (
            <SelecaoResumo totais={selecao.totais} rotulo="pedido(s)" sufixo="selecionado(s)" semValor>
              {pode.editar ? (
                <Button color="primary" size="sm" className="btn-simple" onClick={() => abrirModal("marcadores", { pedidos: idsSelecionados })} disabled={Boolean(acaoEmCurso)}>
                  Marcadores
                </Button>
              ) : null}
              {pode.notfis ? (
                <Button color="primary" size="sm" className="btn-simple" onClick={gerarNotfisSelecao} disabled={Boolean(acaoEmCurso)}>
                  {acaoEmCurso === "notfis" ? <Spinner size="sm" style={{ marginRight: 6 }} /> : null}
                  Gerar NOTFIS
                </Button>
              ) : null}
              {pode.romaneio ? (
                <Button color="primary" size="sm" className="btn-simple" onClick={criarRomaneioSelecao} disabled={Boolean(acaoEmCurso)}>
                  {acaoEmCurso === "romaneio" ? <Spinner size="sm" style={{ marginRight: 6 }} /> : null}
                  Criar romaneio
                </Button>
              ) : null}
              {/* Etiquetas (gerar, imprimir, re-tentar): quem vê a tela gera, sem permissão própria. */}
              <Button color="primary" size="sm" className="btn-simple" onClick={() => abrirModal("etiquetas", { pedidos: idsSelecionados, modo: "gerar" })} disabled={Boolean(acaoEmCurso)}>
                Gerar etiquetas
              </Button>
              <Button color="primary" size="sm" className="btn-simple" onClick={() => imprimirEtiquetas("pdf")} disabled={Boolean(acaoEmCurso)} title="Um PDF com todas as etiquetas emitidas">
                {acaoEmCurso === "pdf" ? <Spinner size="sm" style={{ marginRight: 6 }} /> : null}
                Imprimir etiquetas (PDF)
              </Button>
              <Button color="primary" size="sm" className="btn-simple" onClick={() => imprimirEtiquetas("zpl")} disabled={Boolean(acaoEmCurso)} title="ZPL para impressora térmica">
                {acaoEmCurso === "zpl" ? <Spinner size="sm" style={{ marginRight: 6 }} /> : null}
                Imprimir etiquetas (ZPL)
              </Button>
              {algumComFalha ? (
                <Button color="warning" size="sm" className="btn-simple" onClick={() => abrirModal("etiquetas", { pedidos: idsSelecionados, modo: "retentar" })} disabled={Boolean(acaoEmCurso)}>
                  Re-tentar etiquetas com falha
                </Button>
              ) : null}
              {pode.despacho ? (
                <Button color="primary" size="sm" className="btn-simple" onClick={adicionarAoLoteSelecao} disabled={Boolean(acaoEmCurso)}>
                  {acaoEmCurso === "lote" ? <Spinner size="sm" style={{ marginRight: 6 }} /> : null}
                  Adicionar ao lote
                </Button>
              ) : null}
              <Button color="default" size="sm" outline onClick={selecao.limpar} disabled={Boolean(acaoEmCurso)}>
                Desmarcar seleção
              </Button>
            </SelecaoResumo>
          ) : null}

          {erro ? <div className="pedidos__estado pedidos__estado--erro">{erro}</div> : null}

          {!erro && loading ? (
            <div className="pedidos__estado">
              <Spinner size="sm" style={{ color: AUTOPILOT_COLORS.accent }} /> Consultando pedidos...
            </div>
          ) : null}

          {!erro && !loading && linhas.length === 0 ? (
            <div className="pedidos__estado">
              Nenhum pedido neste recorte. Amplie a janela no botão Período ou limpe os filtros.
            </div>
          ) : null}

          {!erro && !loading && linhas.length > 0 ? (
            <Table className="pedidos__tabela hub-thead-accent" size="sm" responsive>
              <thead className="text-primary">
                <tr>
                  <th className="pedidos__sel">
                    {temAcaoEmMassa ? <CheckboxTodos itens={linhas} selecao={selecao} label="Selecionar todos os pedidos visíveis" /> : null}
                  </th>
                  <th aria-label="Abrir" />
                  <th>Pedido</th>
                  <th>Destinatário</th>
                  <th>Destino</th>
                  <th>Transportadora</th>
                  <th>Emissão</th>
                  <th>Previsão</th>
                  <th className="is-number">Dias em aberto</th>
                  <th>Entregues</th>
                  <th>Status</th>
                  <th aria-label="Ações" />
                </tr>
              </thead>
              <tbody>
                {linhas.map((p) => {
                  const estaAberto = aberto === p.pedido;
                  return (
                    <Fragment key={p.pedido}>
                      <tr
                        className={`pedidos__linha${estaAberto ? " is-open" : ""}${selecao.has(p.id) ? " is-selected" : ""}`}
                        onClick={() => setAberto(estaAberto ? null : p.pedido)}
                        aria-expanded={estaAberto}
                      >
                        {/* Checkbox e menu param o clique: o <tr> inteiro abre a linha. */}
                        <td className="pedidos__sel" onClick={(e) => e.stopPropagation()}>
                          {temAcaoEmMassa ? (
                            <input
                              type="checkbox"
                              aria-label={`Selecionar pedido ${p.pedido}`}
                              checked={selecao.has(p.id)}
                              onChange={() => selecao.toggle(p)}
                            />
                          ) : null}
                        </td>
                        <td className="pedidos__seta">
                          <i
                            className={`tim-icons ${
                              estaAberto ? "icon-minimal-down" : "icon-minimal-right"
                            }`}
                            aria-hidden="true"
                          />
                        </td>
                        <td className="pedidos__col-pedido">
                          <strong>{p.pedido}</strong>
                          <small>
                            {p.quantidade_notas} {Number(p.quantidade_notas) === 1 ? "nota" : "notas"}
                            {p.codigo_rastreio_informado ? ` · ${p.codigo_rastreio_informado}` : ""}
                          </small>
                          {p.marcadoresLista.length ? (
                            <span className="pedidos__chips" title={p.marcadoresLista.join(", ")}>
                              {p.marcadoresLista.map((m) => (
                                <span key={m} className="pedidos__chip">
                                  {m}
                                </span>
                              ))}
                            </span>
                          ) : null}
                        </td>
                        <td title={p.destinatarioExibido}>{p.destinatarioExibido}</td>
                        <td title={p.destinoExibido}>{p.destinoExibido}</td>
                        <td title={p.transportadoraExibida}>{p.transportadoraExibida}</td>
                        <td>{p.emissaoExibida}</td>
                        <td>
                          {p.previsao_entrega ? (
                            p.previsaoExibida
                          ) : (
                            <span className="pedidos__muted">Sem previsão</span>
                          )}
                        </td>
                        {/* Sem previsão não é zero dia em aberto, é não calculável. */}
                        <td className="is-number">
                          {p.dias_em_aberto === null || p.dias_em_aberto === undefined
                            ? "-"
                            : fmtNumero(p.dias_em_aberto)}
                        </td>
                        <td>
                          <div
                            className="pedidos__progresso"
                            title={`${p.encerradas} de ${p.quantidade_notas} nota(s) entregues ou devolvidas`}
                          >
                            <span>
                              {p.encerradas}/{p.quantidade_notas}
                            </span>
                            <div className="pedidos__barra">
                              <i style={{ width: `${p.progresso}%` }} />
                            </div>
                          </div>
                        </td>
                        <td className="pedidos__col-status">
                          <span
                            className={`pedidos__status pedidos__status--${
                              TOM_DO_STATUS[p.status] || "neutral"
                            }`}
                          >
                            {p.status_rotulo}
                          </span>
                          <BadgeEtiquetas p={p} />
                        </td>
                        <td className="pedidos__acoes" onClick={(e) => e.stopPropagation()}>
                          {menuDaLinha(p)}
                        </td>
                      </tr>
                      {estaAberto ? (
                        <tr className="pedidos__detalhe">
                          <td colSpan={colunas}>
                            <NotasDoPedido pedido={p.pedido} paramsRecorte={paramsRecorte} />
                            {p.id_pedido ? <DetalhePedido pedido={p.pedido} versao={versaoDetalhe} /> : null}
                          </td>
                        </tr>
                      ) : null}
                    </Fragment>
                  );
                })}
              </tbody>
            </Table>
          ) : null}

          <TablePagination
            page={pagina}
            pageSize={tamanhoPagina}
            total={total}
            pageSizes={PAGE_SIZES}
            itemLabel="pedido(s)"
            disabled={loading}
            onPageChange={setPagina}
            onPageSizeChange={(tamanho) => {
              setTamanhoPagina(tamanho);
              setPagina(1);
            }}
          />
        </TableSurface>
      </ShellCard>

      {modal && modal.tipo === "operacional" ? <PedidoOperacionalModal pedido={modal.pedido} onClose={fecharModal} /> : null}
      {modal && modal.tipo === "marcadores" ? (
        <MarcadoresModal pedidos={modal.pedidos} marcadoresAtuais={modal.marcadores || []} onClose={fecharModal} />
      ) : null}
      {modal && modal.tipo === "comprovantes" ? (
        <ComprovantesModal pedido={modal.pedido} paramsRecorte={paramsRecorte} podeEditar={pode.editar} onClose={fecharModal} />
      ) : null}
      {modal && modal.tipo === "transportadora" ? <TrocarTransportadoraModal pedido={modal.pedido} onClose={fecharModal} /> : null}
      {modal && modal.tipo === "historico" ? <HistoricoModal pedido={modal.pedido} onClose={fecharModal} /> : null}
      {modal && modal.tipo === "etiquetas" ? (
        <EtiquetasModal modo={modal.modo} pedidos={modal.pedidos} embarcadorId={empresaId} onClose={fecharModal} />
      ) : null}
    </div>
  );
};

export default Pedido;
